import * as React from 'react';
import { useCallback } from 'react';

import styled from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { FloatingLabelInput } from 'components/floating-label-fields';
import { IFloatingLabelInputProps } from 'components/floating-label-fields/types';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { FormValidationState } from 'utils/form';

import { HtmlElement } from './types';

export interface IInputWithButtonProps extends IFloatingLabelInputProps {
  buttonLabel: string;
  onChange: (event: React.ChangeEvent) => void;
  validation?: FormValidationState;
  inputReadOnly?: boolean;
  onSubmit: (inputValue: string) => void;
  isLoading?: boolean;
  isLoadingOrder?: boolean;
  autoFocus?: boolean;
  buttonTabIndex?: number;
}

const sharedStyles = `
  padding: 0;
  margin: 1rem 0;
  position: relative;
  display: flex;
  flex-direction: row;
`;

const InputButtonForm = styled.form`
  ${sharedStyles}
`;

const InputButtonDiv = styled.div`
  ${sharedStyles}
`;

const FloatingInput = styled(FloatingLabelInput)`
  flex-grow: 2;
  margin: 0;
  border-start-start-radius: ${Styles.borderRadius};
  border-start-end-radius: 0;
  border-end-end-radius: 0;
  border-end-start-radius: ${Styles.borderRadius};

  & input {
    padding: 0 1rem;
    border-start-start-radius: ${Styles.borderRadius};
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-end-start-radius: ${Styles.borderRadius};
    border-inline-end: none;
  }

  & label {
    inset-inline-start: 1rem;
    font-size: 0.75rem;
    padding-block-end: 0.125rem;
  }

  & input:focus {
    border: 1px solid ${Styles.color.grey.four};
    border-inline-end: none;
    z-index: ${Styles.zIndex.top};
  }

  label {
    z-index: ${Styles.zIndex.overlay};
  }
`;

const SubmitBtnWrapper = styled.div`
  z-index: ${Styles.zIndex.top};
  display: flex;
  align-items: center;
  justify-content: center;
  button {
    border-start-start-radius: 0;
    border-start-end-radius: ${Styles.borderRadius};
    border-end-end-radius: ${Styles.borderRadius};
    border-end-start-radius: 0;
    padding-inline-start: 1rem;
    padding-inline-end: 1rem;
  }
`;

const StyledActionButton = styled(ActionButton)`
  height: 100%;
`;

const InputWithButton: React.FC<IInputWithButtonProps> = ({
  buttonLabel,
  label,
  value,
  name,
  onChange,
  validation,
  inputReadOnly,
  onSubmit,
  isLoading,
  isLoadingOrder,
  autoFocus,
  disabled = false,
  htmlElement = HtmlElement.FORM,
  buttonTabIndex = 0,
  ...props
}) => {
  // Hack to be able to change the html element in order to use it inside a form
  const isForm = htmlElement === HtmlElement.FORM;

  // Use handleSubmit when using a form
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(value);
  };

  // Use handleClick when using a div
  const handleClick = useCallback(() => {
    onSubmit(value);
  }, [onSubmit, value]);

  const InnerInputButton = (
    <>
      <FloatingInput
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        validation={validation}
        readOnly={inputReadOnly}
        data-testid="input-with-submit-field"
        aria-label={label}
        autoFocus={autoFocus}
        {...props}
      />
      <SubmitBtnWrapper>
        <StyledActionButton
          isLoading={isLoading}
          data-testid="input-with-submit-button"
          variant={ActionButtonVariants.BOX_SHADOW_PRIMARY}
          disabled={disabled}
          tabIndex={buttonTabIndex}
          // If it's not a form, handle submit using onClick and use the button type
          {...(!isForm && { type: 'button', onClick: handleClick })}
        >
          {buttonLabel}
        </StyledActionButton>
      </SubmitBtnWrapper>
    </>
  );

  if (isLoadingOrder) {
    return <CurrencyLineSkeleton />;
  }

  return isForm ? (
    <InputButtonForm onSubmit={handleSubmit} data-testid="input-button-form">
      {InnerInputButton}
    </InputButtonForm>
  ) : (
    <InputButtonDiv data-testid="input-button-div">{InnerInputButton}</InputButtonDiv>
  );
};

export default InputWithButton;
